import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { InvoicePreviewModal } from './invoice-preview-modal.component';
import { SaveInvoiceModal } from './save-invoice-modal.component';
import { flagInvalidDescription } from '../utils/descriptionValidation.utils';
import '../scss/invoice-navigation.scss';

interface InvoiceNavigationProps {
  onPreview?: () => void;
  onSaveDraft?: () => void;
  onSaveAndShare?: () => void;
  isPreviousInvoicingTool?: boolean;
  onTogglePreviousInvoicingTool?: (enabled: boolean) => void;
  isEditMode?: boolean;
  isPreviewLoading?: boolean;
  getPayload?: () => any;
  onSecondApiCall?: (pdfBlob: Blob) => Promise<void>;
  validateServiceDescriptionsRef?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
  dataRef?: React.MutableRefObject<any>;
  onStartTour?: () => void;
  /** Show the "Start Tour" button. Only true for a fresh new invoice — hidden
   *  in edit / draft / edit-request / timesheet flows. */
  showStartTour?: boolean;
}

export const InvoiceNavigation: React.FC<InvoiceNavigationProps> = ({
  onPreview,
  onSaveDraft,
  onSaveAndShare,
  isPreviousInvoicingTool = false,
  onTogglePreviousInvoicingTool,
  isEditMode,
  isPreviewLoading = false,
  getPayload,
  onSecondApiCall,
  validateServiceDescriptionsRef,
  dataRef,
  onStartTour,
  showStartTour = true,
}) => {
  const { t, i18n } = useTranslation();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);

  // Language-based "how to create an invoice" guide video. Swedish users get the
  // sv clip; everyone else gets the English one.
  const isSwedish = (i18n.language || '').toLowerCase().startsWith('sv');
  const guideVideoUrl = isSwedish
    ? 'https://invozioprod.blob.core.windows.net/files/create_new_invoice_sv.mp4'
    : 'https://invozioprod.blob.core.windows.net/files/create_new_invoice_eng.mp4';

  // Helper function to validate description
  const validateDescription = (): boolean => {
    const invoiceData = dataRef?.current;
    const description = invoiceData?.formData?.otherDetails?.customerNotes || ""; 
    if (!description || description.trim().length === 0) {
      toast.error(String(t("PLEASE_ENTER_DESCRIPTION_BEFORE_CREATING_INVOICE") || "Please enter customer notes (description) before creating invoice"));
      flagInvalidDescription();
      return false;
    }

    const descriptionLength = description.trim().length;
    const wordCount = description.trim().split(/\s+/).filter((word: string) => word.length > 0).length;

    if (descriptionLength < 20 && wordCount < 3) {
      toast.error(String(t("Enter at least 20 characters or 3 words") || "Enter at least 20 characters or 3 words in customer notes"));
      flagInvalidDescription();
      return false;
    }

    return true;
  };

  const handlePreviewClick = () => {
    // 1. Validate description first
    if (!validateDescription()) {
      return;
    }

    // 2. Validate service descriptions for all freelancers
    if (validateServiceDescriptionsRef?.current) {
      const validationFunctions = validateServiceDescriptionsRef.current;
      const freelancerIds = Object.keys(validationFunctions);
      
      for (const freelancerId of freelancerIds) {
        const validateFunc = validationFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t("PLEASE_FILL_SERVICE_DESCRIPTION") || "Please fill in service description for all working hours"));
          return;
        }
      }
    }
    
    setShowPreviewModal(true);
    if (onPreview) {
      onPreview();
    }
  };

  const handleSaveDraftClick = () => {
    // 1. Validate description first
    if (!validateDescription()) {
      return;
    }

    // 2. Validate service descriptions for all freelancers
    if (validateServiceDescriptionsRef?.current) {
      const validationFunctions = validateServiceDescriptionsRef.current;
      const freelancerIds = Object.keys(validationFunctions);
      
      for (const freelancerId of freelancerIds) {
        const validateFunc = validationFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t("PLEASE_FILL_SERVICE_DESCRIPTION") || "Please fill in service description for all working hours"));
          return;
        }
      }
    }
    
    setShowSaveModal(true);
  };

  const handleConfirmSave = () => {
    if (onSaveDraft) {
      onSaveDraft();
    }
  };

  return (
    <>
    <div className="invoice-navigation">
      <div className="invoice-nav-left">
        <div className="create-invoice-section">
          <div className="document-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <path d="M14 2v6h6" /><path d="M9 13h6M9 17h4" />
            </svg>
          </div>
          <div className="nav-title-wrap">
            <span className="nav-title">{isEditMode === true ? t('EDIT_INVOICE') : t('CREATE_NEW_INVOICE')}</span>
          </div>
        </div>
        
        {/* <div className="nav-divider"></div> */}
        
        {/* <div className="invoicing-tool-toggle">
          <span className="toggle-label">{t('PREVIOUS_INVOICING_TOOL')}</span>
          <div className={`toggle-switch ${isPreviousInvoicingTool ? 'active' : ''}`}>
            <div className="toggle-slider"></div>
          </div>
        </div> */}
      </div>
      
     
        <div className="invoice-nav-right">
          {!isEditMode && (
            <button
              type="button"
              className="iv-guide-btn"
              onClick={() => setShowGuideModal(true)}
              title={t('WATCH_GUIDE_FULL') || 'New here? Watch a 6-minute guide on creating invoices.'}
            >
              <span className="iv-guide-btn__play" aria-hidden="true">
                {/* Screen-with-play → reads as "watch a video guide", distinct from
                    the plain play triangles used by Preview / Start Tour. */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="2.5" y="5" width="19" height="14" rx="3" stroke="currentColor" strokeWidth="1.9" />
                  <path d="M10.2 9.3v5.4l4.4-2.7-4.4-2.7z" fill="currentColor" />
                </svg>
              </span>
              <span className="iv-guide-btn__text">{t('WATCH_GUIDE_BTN') || 'New here? Watch a 6-minute guide'}</span>
            </button>
          )}
          <button
            className="nav-btn preview-btn"
            onClick={handlePreviewClick}
            disabled={isPreviewLoading}
            style={{
              opacity: isPreviewLoading ? 0.7 : 1,
              cursor: isPreviewLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isPreviewLoading ? (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="8" cy="8" r="6" stroke="#544196" strokeWidth="2" strokeDasharray="30" strokeLinecap="round" opacity="0.3"/>
                  <circle cx="8" cy="8" r="6" stroke="#544196" strokeWidth="2" strokeDasharray="10 20" strokeLinecap="round"/>
                </svg>
                {t('LOADING')}
              </>
            ) : (
              <>
                 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M4.66797 11.9566V4.03891C4.66797 2.93268 5.93743 2.3076 6.81425 2.98208L11.9608 6.94093C12.6546 7.47466 12.6546 8.52087 11.9608 9.05459L6.81425 13.0134C5.93742 13.6879 4.66797 13.0628 4.66797 11.9566Z" stroke="#544196" stroke-width="2" stroke-linecap="round"/>
</svg>
                {t('PREVIEW')}
              </>
            )}
          </button>
           {!isEditMode && (
          <button className="nav-btn save-draft-btn" onClick={handleSaveDraftClick}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M13.1893 2.06395L14.0803 1.60996L14.0803 1.60996L13.1893 2.06395ZM11.2013 1.33594L11.2013 2.33594L11.2013 2.33594L11.2013 1.33594ZM12.6066 1.48126L13.0606 0.590257L13.0606 0.590256L12.6066 1.48126ZM12.6066 14.5239L13.0606 15.415L13.0606 15.415L12.6066 14.5239ZM13.1893 13.9413L14.0803 14.3952L14.0803 14.3952L13.1893 13.9413ZM3.39598 14.5239L2.94199 15.415L2.94199 15.415L3.39598 14.5239ZM2.81329 13.9413L3.7043 13.4873L3.7043 13.4873L2.81329 13.9413ZM3.0585 5.61205L2.35139 4.90494L2.35139 4.90494L3.0585 5.61205ZM6.94411 1.72646L6.23701 1.01935L6.23701 1.01935L6.94411 1.72646ZM4.8013 14.6693V15.6693H11.2013V14.6693V13.6693H4.8013V14.6693ZM13.3346 12.5359H14.3346V3.46927H13.3346H12.3346V12.5359H13.3346ZM13.3346 3.46927H14.3346C14.3346 3.1124 14.3354 2.78461 14.3131 2.51211C14.2899 2.22793 14.2368 1.91704 14.0803 1.60996L13.1893 2.06395L12.2983 2.51794C12.2872 2.49608 12.3067 2.51448 12.3198 2.67497C12.3339 2.84714 12.3346 3.0794 12.3346 3.46927H13.3346ZM11.2013 1.33594L11.2013 2.33594C11.5912 2.33594 11.8234 2.33672 11.9956 2.35078C12.1561 2.36389 12.1745 2.38341 12.1526 2.37227L12.6066 1.48126L13.0606 0.590256C12.7535 0.433793 12.4426 0.380642 12.1585 0.357424C11.886 0.33516 11.5582 0.335937 11.2013 0.335938L11.2013 1.33594ZM13.1893 2.06395L14.0803 1.60996C13.8566 1.17091 13.4997 0.81396 13.0606 0.590257L12.6066 1.48126L12.1526 2.37227C12.2154 2.40423 12.2663 2.45522 12.2983 2.51794L13.1893 2.06395ZM11.2013 14.6693V15.6693C11.5582 15.6693 11.886 15.67 12.1585 15.6478C12.4426 15.6246 12.7535 15.5714 13.0606 15.415L12.6066 14.5239L12.1526 13.6329C12.1745 13.6218 12.1561 13.6413 11.9956 13.6544C11.8234 13.6685 11.5912 13.6693 11.2013 13.6693V14.6693ZM13.3346 12.5359H12.3346C12.3346 12.9258 12.3339 13.1581 12.3198 13.3302C12.3067 13.4907 12.2872 13.5091 12.2983 13.4873L13.1893 13.9413L14.0803 14.3952C14.2368 14.0882 14.2899 13.7773 14.3131 13.4931C14.3354 13.2206 14.3346 12.8928 14.3346 12.5359H13.3346ZM12.6066 14.5239L13.0606 15.415C13.4997 15.1912 13.8566 14.8343 14.0803 14.3952L13.1893 13.9413L12.2983 13.4873C12.2663 13.55 12.2154 13.601 12.1526 13.6329L12.6066 14.5239ZM4.8013 14.6693V13.6693C4.41144 13.6693 4.17918 13.6685 4.00701 13.6544C3.8465 13.6413 3.82811 13.6218 3.84997 13.6329L3.39598 14.5239L2.94199 15.415C3.24907 15.5714 3.55997 15.6246 3.84415 15.6478C4.11665 15.67 4.44444 15.6693 4.8013 15.6693V14.6693ZM2.66797 12.5359H1.66797C1.66797 12.8928 1.66719 13.2206 1.68946 13.4931C1.71267 13.7773 1.76582 14.0882 1.92229 14.3952L2.81329 13.9413L3.7043 13.4873C3.71544 13.5091 3.69593 13.4907 3.68281 13.3302C3.66875 13.1581 3.66797 12.9258 3.66797 12.5359H2.66797ZM3.39598 14.5239L3.84997 13.6329C3.78725 13.601 3.73626 13.55 3.7043 13.4873L2.81329 13.9413L1.92229 14.3952C2.14599 14.8343 2.50295 15.1912 2.94199 15.415L3.39598 14.5239ZM2.66797 6.55486H1.66797V12.5359H2.66797H3.66797V6.55486H2.66797ZM11.2013 1.33594V0.335938H7.88692V1.33594V2.33594H11.2013V1.33594ZM3.0585 5.61205L3.7656 6.31916L7.65122 2.43357L6.94411 1.72646L6.23701 1.01935L2.35139 4.90494L3.0585 5.61205ZM3.33464 6.0026V7.0026H6.61669V6.0026V5.0026H3.33464V6.0026ZM7.33464 5.28465H8.33464V2.0026H7.33464H6.33464V5.28465H7.33464ZM6.61669 6.0026V7.0026C7.56549 7.0026 8.33464 6.23344 8.33464 5.28465H7.33464H6.33464C6.33464 5.12889 6.46091 5.0026 6.61669 5.0026V6.0026ZM7.88692 1.33594V0.335938C7.55024 0.335938 7.22102 0.408722 6.92044 0.54551L7.33464 1.4557L7.74884 2.36588C7.79185 2.34631 7.83882 2.33594 7.88692 2.33594V1.33594ZM7.33464 1.4557L6.92044 0.54551C6.66849 0.660164 6.43651 0.819847 6.23701 1.01935L6.94411 1.72646L7.65122 2.43357C7.67974 2.40505 7.71282 2.38227 7.74884 2.36588L7.33464 1.4557ZM7.33464 2.0026H8.33464V1.4557H7.33464H6.33464V2.0026H7.33464ZM2.66797 6.55486H3.66797C3.66797 6.50677 3.67834 6.45979 3.69791 6.41679L2.78772 6.0026L1.87752 5.58842C1.74075 5.889 1.66797 6.2182 1.66797 6.55486H2.66797ZM2.78772 6.0026L3.69791 6.41679C3.7143 6.38077 3.73708 6.34768 3.7656 6.31916L3.0585 5.61205L2.35139 4.90494C2.15187 5.10446 1.99218 5.33646 1.87752 5.58842L2.78772 6.0026ZM3.33464 6.0026V5.0026H2.78772V6.0026V7.0026H3.33464V6.0026Z" fill="#544196"/>
</svg>
            {t('SAVE_DRAFT')}
          </button> )}

          {showStartTour && onStartTour && (
            <button type="button" className="iv-start-tour-btn" onClick={onStartTour}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M5.83 14.95V5.05c0-1.38 1.59-2.16 2.68-1.32l6.43 4.95c.87.67.87 1.98 0 2.65l-6.43 4.95c-1.09.84-2.68.06-2.68-1.33z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              </svg>
              <span>{t('START_TOUR') || 'Start Tour'}</span>
            </button>
          )}

          {/* <button className="nav-btn save-share-btn primary" onClick={onSaveAndShare}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M14.6667 10V12.6667C14.6667 13.0203 14.5262 13.3594 14.2761 13.6095C14.0261 13.8595 13.687 14 13.3333 14H2.66667C2.31305 14 1.97391 13.8595 1.72386 13.6095C1.47381 13.3594 1.33333 13.0203 1.33333 12.6667V10M4 6.66667L8 2.66667M8 2.66667L12 6.66667M8 2.66667V10" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('SAVE_AND_SHARE')}
          </button> */}
        </div>
     
    </div>

    {/* <InvoicePreviewModal
      isOpen={showPreviewModal}
      onClose={() => setShowPreviewModal(false)}
    /> */}

    <SaveInvoiceModal
      isOpen={showSaveModal}
      onClose={() => setShowSaveModal(false)}
      onSave={handleConfirmSave}
      getPayload={getPayload}
      onSecondApiCall={onSecondApiCall}
    />

    {/* Guide video modal — plays the language-based "how to create an invoice" clip */}
    {showGuideModal && (
      <div className="iv-guide-modal-overlay" onClick={() => setShowGuideModal(false)}>
        <div className="iv-guide-modal" onClick={(e) => e.stopPropagation()}>
          <div className="iv-guide-modal__head">
            <h3 className="iv-guide-modal__title">{t('WATCH_GUIDE_MODAL_TITLE') || 'How to create an invoice'}</h3>
            <button
              type="button"
              className="iv-guide-modal__close"
              onClick={() => setShowGuideModal(false)}
              aria-label={t('CLOSE') || 'Close'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
          <div className="iv-guide-modal__video-wrap">
            <video
              key={guideVideoUrl}
              className="iv-guide-modal__video"
              src={guideVideoUrl}
              controls
              autoPlay
              playsInline
              controlsList="nodownload"
            />
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default InvoiceNavigation;